import type { Floor, FloorCell, TurnoutCell, TurnoutRollup } from '../api'

/**
 * The analytics model — every number the console draws, derived once, in one place.
 *
 * ## Why the derivation is client-side and not another endpoint
 *
 * `GET /turnout/elections/{id}/floor` already ships the whole polling day: each cell
 * carries its cumulative count for every hour. That is the same payload the Floor console
 * scrubs, and it is what lets this page recompute *every* chart at an arbitrary hour
 * without a request. An "analytics at hour H" endpoint would put a round trip inside a
 * slider drag, and would disagree with the Floor console the first time one of them was
 * changed.
 *
 * ## The one rule this file exists to enforce
 *
 * **Pace is recomputed from the scrubbed hour, never taken from `day.elapsedShare`.**
 * `elapsedShare` describes when the payload was built. Scrubbing back to 11:00 with the
 * close-of-poll rule still applied would report every cell as behind all morning, which
 * is the single most misleading thing this page could say to somebody deciding where to
 * send a van.
 */

/** A cell with its geography joined on and its position at the scrubbed hour. */
export interface CellView {
  id: string
  code: string
  label: string | null
  stationId: string
  stationName: string | null
  statusCode: string
  coordinatorName: string | null
  meetingPoint: string | null
  members: number
  pledged: number
  /** Cumulative voted AT THE SCRUBBED HOUR — not the payload's final figure. */
  voted: number
  /** This hour's own count. A cumulative curve alone cannot show a stall. */
  votedInHour: number
  inQueue: number
  enRoute: number
  outstanding: number
  transportGaps: number
  escalationsOpen: number
  stalledMinutes: number | null
  /** voted ÷ members, 0..1. */
  share: number
  /** Where the cell should be by now, 0..1 — from the scrubbed hour, not the payload. */
  paceShare: number
  /** `paceShare − share`. Positive is behind; the sign is the point. */
  behind: number
  /** Members an hour it must still move to finish. Null once the polls have shut. */
  perHourNeeded: number | null
  latitude: number | null
  longitude: number | null
  series: number[]
}

export interface Derived {
  hours: string[]
  /** Index into `hours` the whole page is drawn at. */
  hour: number
  cells: CellView[]
  stations: StationView[]
  /** Votes per hour up to and including the scrubbed hour. */
  beats: { at: string; votedInHour: number; cumulative: number; requiredPace: number }[]
  totals: {
    members: number
    pledged: number
    voted: number
    inQueue: number
    enRoute: number
    outstanding: number
    transportGaps: number
    escalationsOpen: number
    escalationsLate: number
    cellsBehind: number
    cellsStalled: number
    cellsNoCoordinator: number
    cellsWithoutCoords: number
    share: number
    paceShare: number
  }
  /** True once the scrubbed hour is past the last hour of the poll window. */
  closed: boolean
}

export interface StationView {
  id: string
  name: string
  cells: number
  members: number
  voted: number
  outstanding: number
  inQueue: number
  behindCells: number
  share: number
  latitude: number | null
  longitude: number | null
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)

/**
 * Join the Floor payload to the cell register and freeze it at one hour.
 *
 * `listCells` is the only source of coordinates — the Floor payload carries counts, not
 * geography — so the join is what makes a map possible at all. A cell present on the
 * floor but missing from the register keeps null coordinates and is counted in
 * `cellsWithoutCoords` rather than dropped: a map that silently omits cells reads as the
 * complete picture, and the ones nobody has placed are exactly the ones to notice.
 */
export function derive(
  floor: Floor,
  register: TurnoutCell[],
  hourIndex: number,
  stationFilter: string | null,
): Derived {
  const hours = floor.hours
  const last = Math.max(hours.length - 1, 0)
  const h = Math.max(0, Math.min(hourIndex, last))
  const geo = new Map(register.map((c) => [c.id, c]))

  // The pace rule, recomputed from the scrubbed hour. `h + 1` because the hour at index
  // h has BEEN counted — at the first bucket one hour of the window has elapsed.
  const paceShare = hours.length === 0 ? 0 : clamp01((h + 1) / hours.length)
  const closed = !floor.day.pollsOpen || h >= last
  const hoursLeft = Math.max(hours.length - (h + 1), 0)

  const cells: CellView[] = floor.cells
    .filter((c) => !stationFilter || c.stationId === stationFilter)
    .map((c) => project(c, geo.get(c.id), h, paceShare, hoursLeft, closed))

  const stationGeo = new Map<string, { lat: number | null; lon: number | null }>()
  for (const c of register) {
    if (c.latitude == null || c.longitude == null) continue
    const acc = stationGeo.get(c.stationId)
    if (!acc) stationGeo.set(c.stationId, { lat: c.latitude, lon: c.longitude })
  }

  const byStation = new Map<string, StationView>()
  for (const c of cells) {
    const key = c.stationId
    const s = byStation.get(key) ?? {
      id: key,
      name: c.stationName ?? 'Unnamed station',
      cells: 0, members: 0, voted: 0, outstanding: 0, inQueue: 0, behindCells: 0, share: 0,
      latitude: stationGeo.get(key)?.lat ?? null,
      longitude: stationGeo.get(key)?.lon ?? null,
    }
    s.cells += 1
    s.members += c.members
    s.voted += c.voted
    s.outstanding += c.outstanding
    s.inQueue += c.inQueue
    if (c.behind > 0.05) s.behindCells += 1
    byStation.set(key, s)
  }
  const stations = [...byStation.values()]
    .map((s) => ({ ...s, share: s.members === 0 ? 0 : s.voted / s.members }))
    .sort((a, b) => b.members - a.members)

  const beats = floor.series.slice(0, h + 1).map((b, i) => ({
    at: b.at,
    votedInHour: b.votedInHour,
    cumulative: b.cumulativeVoted,
    // The payload's `requiredPace` is computed against the whole window and is the right
    // line here — it is the rule, not a description of when the payload was built.
    requiredPace: b.requiredPace || Math.round(((i + 1) / Math.max(hours.length, 1)) * sumMembers(cells)),
  }))

  const sum = (f: (c: CellView) => number) => cells.reduce((n, c) => n + f(c), 0)
  const members = sum((c) => c.members)
  const voted = sum((c) => c.voted)

  return {
    hours, hour: h, cells, stations, beats, closed,
    totals: {
      members,
      pledged: sum((c) => c.pledged),
      voted,
      inQueue: sum((c) => c.inQueue),
      enRoute: sum((c) => c.enRoute),
      outstanding: Math.max(members - voted, 0),
      transportGaps: sum((c) => c.transportGaps),
      escalationsOpen: sum((c) => c.escalationsOpen),
      escalationsLate: floor.escalations.filter((e) => (e.minutesLate ?? 0) > 0).length,
      cellsBehind: cells.filter((c) => c.behind > 0.05).length,
      cellsStalled: cells.filter((c) => (c.stalledMinutes ?? 0) >= 90).length,
      cellsNoCoordinator: cells.filter((c) => !c.coordinatorName).length,
      cellsWithoutCoords: cells.filter((c) => c.latitude == null || c.longitude == null).length,
      share: members === 0 ? 0 : voted / members,
      paceShare,
    },
  }
}

function sumMembers(cells: CellView[]) {
  return cells.reduce((n, c) => n + c.members, 0)
}

function project(
  c: FloorCell, reg: TurnoutCell | undefined, h: number,
  paceShare: number, hoursLeft: number, closed: boolean,
): CellView {
  // `series` is cumulative and aligned to `hours`. Falling back to the payload's final
  // `voted` when the series is short keeps a cell with a truncated series on the board
  // rather than drawing it as having done nothing.
  const votedAt = c.series[h] ?? c.voted
  const prev = h > 0 ? (c.series[h - 1] ?? 0) : 0
  const share = c.members === 0 ? 0 : clamp01(votedAt / c.members)
  const remaining = Math.max(c.members - votedAt, 0)
  return {
    id: c.id,
    code: c.code,
    label: c.label,
    stationId: c.stationId,
    stationName: c.stationName,
    statusCode: c.statusCode,
    coordinatorName: c.coordinatorName,
    meetingPoint: c.meetingPoint,
    members: c.members,
    pledged: c.pledged,
    voted: votedAt,
    votedInHour: Math.max(votedAt - prev, 0),
    inQueue: c.inQueue,
    enRoute: c.enRoute,
    outstanding: remaining,
    transportGaps: c.transportGaps,
    escalationsOpen: c.escalationsOpen,
    stalledMinutes: c.stalledMinutes,
    share,
    paceShare,
    behind: paceShare - share,
    // Null once there is no time left to use the number. "Badly behind" at 2pm is an
    // emergency; the same cell at 6pm is a result, and a demand per hour with no hours
    // remaining is a division by zero dressed as advice.
    perHourNeeded: closed || hoursLeft === 0 ? null : Math.ceil(remaining / hoursLeft),
    latitude: reg?.latitude ?? null,
    longitude: reg?.longitude ?? null,
    series: c.series,
  }
}

/** The share of `total` held by the top `n` rows of a descending list — a Pareto read. */
export function concentration(values: number[], n: number): number {
  const total = values.reduce((a, b) => a + b, 0)
  if (total <= 0) return 0
  return [...values].sort((a, b) => b - a).slice(0, n).reduce((a, b) => a + b, 0) / total
}

/** Marginal cost of one more vote, from the rollup's incentive spend. Null if unfunded. */
export function costPerVote(rollup: TurnoutRollup | null, voted: number): number | null {
  const spend = Number(rollup?.incentiveSpend ?? 0)
  if (!spend || voted <= 0) return null
  return spend / voted
}
